import type { ModelProps, ModelRegistry } from '../types';
import { useGameStore } from '@hollowveil/shared';
import { useRef, useState, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

// ============================================
// STRANGER THINGS - PROPS & VEHICLES
// ============================================

// ===== 80s PROPS & ITEMS =====

export function VintageBikeModel({ properties }: ModelProps) {
  const bikeColor = properties?.color === 'blue' ? '#4169E1' :
                    properties?.color === 'yellow' ? '#FFD700' : '#DC143C';
  return (
    <group>
      <mesh position={[-0.3, 0.25, 0]} rotation={[Math.PI / 2, 0, 0]} castShadow>
        <torusGeometry args={[0.25, 0.03, 8, 16]} />
        <meshStandardMaterial color="#333333" roughness={0.8} />
      </mesh>
      <mesh position={[0.3, 0.25, 0]} rotation={[Math.PI / 2, 0, 0]} castShadow>
        <torusGeometry args={[0.25, 0.03, 8, 16]} />
        <meshStandardMaterial color="#333333" roughness={0.8} />
      </mesh>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <mesh key={`spoke-l-${i}`} position={[-0.3, 0.25, 0]} rotation={[Math.PI / 2, 0, (i * Math.PI) / 3]}>
          <cylinderGeometry args={[0.005, 0.005, 0.5, 4]} />
          <meshStandardMaterial color="#888888" metalness={0.8} />
        </mesh>
      ))}
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <mesh key={`spoke-r-${i}`} position={[0.3, 0.25, 0]} rotation={[Math.PI / 2, 0, (i * Math.PI) / 3]}>
          <cylinderGeometry args={[0.005, 0.005, 0.5, 4]} />
          <meshStandardMaterial color="#888888" metalness={0.8} />
        </mesh>
      ))}
      <mesh position={[0, 0.35, 0]} rotation={[0, 0, 0.3]} castShadow>
        <cylinderGeometry args={[0.02, 0.02, 0.7, 6]} />
        <meshStandardMaterial color={bikeColor} metalness={0.6} roughness={0.3} />
      </mesh>
      <mesh position={[-0.15, 0.45, 0]} rotation={[0, 0, -0.5]} castShadow>
        <cylinderGeometry args={[0.02, 0.02, 0.35, 6]} />
        <meshStandardMaterial color={bikeColor} metalness={0.6} roughness={0.3} />
      </mesh>
      <mesh position={[-0.25, 0.55, 0]} rotation={[Math.PI / 2, 0, 0]} castShadow>
        <cylinderGeometry args={[0.015, 0.015, 0.3, 6]} />
        <meshStandardMaterial color="#333333" metalness={0.7} />
      </mesh>
      <mesh position={[0.2, 0.5, 0]} castShadow>
        <boxGeometry args={[0.15, 0.04, 0.08]} />
        <meshStandardMaterial color="#2F1810" roughness={0.9} />
      </mesh>
      <mesh position={[0, 0.2, 0.08]} castShadow>
        <boxGeometry args={[0.06, 0.02, 0.04]} />
        <meshStandardMaterial color="#666666" metalness={0.5} />
      </mesh>
    </group>
  );
}

export function WalkieTalkieModel(): JSX.Element {
  return (
    <group>
      <mesh position={[0, 0.12, 0]} castShadow>
        <boxGeometry args={[0.08, 0.2, 0.04]} />
        <meshStandardMaterial color="#2C2C2C" roughness={0.7} />
      </mesh>
      <mesh position={[0.02, 0.28, 0]} castShadow>
        <cylinderGeometry args={[0.005, 0.003, 0.12, 6]} />
        <meshStandardMaterial color="#1a1a1a" metalness={0.8} />
      </mesh>
      <mesh position={[0, 0.16, 0.021]}>
        <boxGeometry args={[0.05, 0.06, 0.002]} />
        <meshStandardMaterial color="#444444" roughness={0.5} />
      </mesh>
      <mesh position={[-0.025, 0.1, 0.02]} castShadow>
        <boxGeometry args={[0.02, 0.04, 0.01]} />
        <meshStandardMaterial color="#8B0000" roughness={0.6} />
      </mesh>
      <mesh position={[0.025, 0.2, 0.021]}>
        <sphereGeometry args={[0.008, 8, 8]} />
        <meshStandardMaterial color="#00FF00" emissive="#00FF00" emissiveIntensity={0.5} />
      </mesh>
    </group>
  );
}

export function FlashlightModel({ hovered }: ModelProps) {
  return (
    <group rotation={[0, 0, Math.PI / 6]}>
      <mesh position={[0, 0.1, 0]} castShadow>
        <cylinderGeometry args={[0.025, 0.025, 0.2, 12]} />
        <meshStandardMaterial color="#1a1a1a" metalness={0.9} roughness={0.2} />
      </mesh>
      <mesh position={[0, 0.22, 0]} castShadow>
        <cylinderGeometry args={[0.04, 0.03, 0.05, 12]} />
        <meshStandardMaterial color="#2a2a2a" metalness={0.9} roughness={0.2} />
      </mesh>
      <mesh position={[0, 0.25, 0]}>
        <circleGeometry args={[0.035, 16]} />
        <meshStandardMaterial
          color="#FFFFCC"
          emissive="#FFFF88"
          emissiveIntensity={hovered ? 0.8 : 0.3}
          transparent
          opacity={0.8}
        />
      </mesh>
      <mesh position={[0.03, 0.05, 0]} castShadow>
        <cylinderGeometry args={[0.008, 0.008, 0.01, 8]} />
        <meshStandardMaterial color="#333333" roughness={0.5} />
      </mesh>
    </group>
  );
}

export function MailboxModel(): JSX.Element {
  return (
    <group>
      <mesh position={[0, 0.5, 0]} castShadow>
        <cylinderGeometry args={[0.04, 0.05, 1, 8]} />
        <meshStandardMaterial color="#8B4513" roughness={0.9} />
      </mesh>
      <mesh position={[0, 1.05, 0]} rotation={[0, 0, Math.PI / 2]} castShadow>
        <cylinderGeometry args={[0.15, 0.15, 0.35, 12, 1, false, 0, Math.PI]} />
        <meshStandardMaterial color="#2F4F4F" metalness={0.3} roughness={0.6} />
      </mesh>
      <mesh position={[-0.17, 1.05, 0]} rotation={[0, Math.PI / 2, 0]}>
        <circleGeometry args={[0.15, 12]} />
        <meshStandardMaterial color="#2F4F4F" metalness={0.3} roughness={0.6} />
      </mesh>
      <mesh position={[0.17, 1.05, 0]} rotation={[0, -Math.PI / 2, 0]}>
        <circleGeometry args={[0.14, 12]} />
        <meshStandardMaterial color="#1C3030" metalness={0.4} roughness={0.5} />
      </mesh>
      <mesh position={[0.1, 1.15, 0.16]} rotation={[0, 0, -0.3]} castShadow>
        <boxGeometry args={[0.08, 0.02, 0.12]} />
        <meshStandardMaterial color="#DC143C" roughness={0.6} />
      </mesh>
    </group>
  );
}

export function ArcadeCabinetModel({ hovered }: ModelProps) {
  return (
    <group>
      <mesh position={[0, 0.8, 0]} castShadow>
        <boxGeometry args={[0.6, 1.6, 0.5]} />
        <meshStandardMaterial color="#1a1a2e" roughness={0.7} />
      </mesh>
      <mesh position={[0, 1.1, 0.26]}>
        <boxGeometry args={[0.4, 0.35, 0.02]} />
        <meshStandardMaterial color="#000033" emissive="#0066FF" emissiveIntensity={hovered ? 0.6 : 0.3} />
      </mesh>
      <mesh position={[0, 1.45, 0.2]}>
        <boxGeometry args={[0.5, 0.2, 0.15]} />
        <meshStandardMaterial color="#FFD700" emissive="#FF6600" emissiveIntensity={0.4} />
      </mesh>
      <mesh position={[0, 0.65, 0.3]} rotation={[-0.3, 0, 0]} castShadow>
        <boxGeometry args={[0.5, 0.15, 0.25]} />
        <meshStandardMaterial color="#2a2a3e" roughness={0.6} />
      </mesh>
      <mesh position={[-0.1, 0.72, 0.32]} castShadow>
        <sphereGeometry args={[0.03, 8, 8]} />
        <meshStandardMaterial color="#FF0000" roughness={0.4} />
      </mesh>
      <mesh position={[-0.1, 0.68, 0.32]} castShadow>
        <cylinderGeometry args={[0.015, 0.015, 0.06, 8]} />
        <meshStandardMaterial color="#333333" metalness={0.7} />
      </mesh>
      {[0, 1, 2].map((i) => (
        <mesh key={`btn-${i}`} position={[0.05 + i * 0.06, 0.7, 0.32]} castShadow>
          <cylinderGeometry args={[0.025, 0.025, 0.02, 12]} />
          <meshStandardMaterial color={['#FF0000', '#FFFF00', '#00FF00'][i]} roughness={0.3} />
        </mesh>
      ))}
    </group>
  );
}

export function EggoBoxModel(): JSX.Element {
  return (
    <group>
      <mesh position={[0, 0.08, 0]} castShadow>
        <boxGeometry args={[0.2, 0.12, 0.08]} />
        <meshStandardMaterial color="#FFD700" roughness={0.7} />
      </mesh>
      <mesh position={[0, 0.08, 0.041]}>
        <boxGeometry args={[0.12, 0.06, 0.001]} />
        <meshStandardMaterial color="#FF4500" />
      </mesh>
      <mesh position={[0, 0.08, 0.042]}>
        <boxGeometry args={[0.08, 0.03, 0.001]} />
        <meshStandardMaterial color="#FFFFFF" />
      </mesh>
    </group>
  );
}

// ===== 80s VEHICLES =====

export function Car80sModel({ properties, isOccluding, opacity = 1 }: ModelProps) {
  const carColor = properties?.color === 'blue' ? '#4169E1' :
                   properties?.color === 'red' ? '#B22222' : '#8B4513';
  const carColorDark = properties?.color === 'blue' ? '#2a4a9a' :
                       properties?.color === 'red' ? '#8B1A1A' : '#5a3010';
  return (
    <group>
      <mesh position={[0, 0.08, 0]} castShadow>
        <boxGeometry args={[1.4, 0.08, 0.6]} />
        <meshStandardMaterial color="#1a1a1a" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0.35, 0.22, 0]} castShadow>
        <boxGeometry args={[0.5, 0.2, 0.58]} />
        <meshStandardMaterial color={carColor} metalness={0.7} roughness={0.3} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[-0.1, 0.25, 0]} castShadow>
        <boxGeometry args={[0.6, 0.26, 0.58]} />
        <meshStandardMaterial color={carColor} metalness={0.7} roughness={0.3} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[-0.5, 0.2, 0]} castShadow>
        <boxGeometry args={[0.35, 0.18, 0.56]} />
        <meshStandardMaterial color={carColor} metalness={0.7} roughness={0.3} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[-0.05, 0.5, 0]} castShadow>
        <boxGeometry args={[0.55, 0.24, 0.52]} />
        <meshStandardMaterial color={carColor} metalness={0.7} roughness={0.3} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {[[0.2, 0.42, 0.27], [0.2, 0.42, -0.27], [-0.28, 0.42, 0.27], [-0.28, 0.42, -0.27]].map((pos, i) => (
        <mesh key={`pillar-${i}`} position={pos as [number, number, number]} castShadow>
          <boxGeometry args={[0.04, 0.2, 0.04]} />
          <meshStandardMaterial color={carColorDark} metalness={0.6} roughness={0.4} transparent={isOccluding} opacity={opacity} />
        </mesh>
      ))}
      <mesh position={[0.22, 0.48, 0]} rotation={[0, 0, 0.4]}>
        <boxGeometry args={[0.25, 0.02, 0.46]} />
        <meshStandardMaterial color="#87CEEB" transparent opacity={isOccluding ? opacity * 0.5 : 0.5} metalness={0.9} roughness={0.1} />
      </mesh>
      <mesh position={[-0.30, 0.48, 0]} rotation={[0, 0, -0.3]}>
        <boxGeometry args={[0.2, 0.02, 0.46]} />
        <meshStandardMaterial color="#87CEEB" transparent opacity={isOccluding ? opacity * 0.5 : 0.5} metalness={0.9} roughness={0.1} />
      </mesh>
      <mesh position={[-0.05, 0.5, 0.265]}>
        <boxGeometry args={[0.45, 0.16, 0.01]} />
        <meshStandardMaterial color="#6BA3D6" transparent opacity={isOccluding ? opacity * 0.4 : 0.4} metalness={0.9} />
      </mesh>
      <mesh position={[-0.05, 0.5, -0.265]}>
        <boxGeometry args={[0.45, 0.16, 0.01]} />
        <meshStandardMaterial color="#6BA3D6" transparent opacity={isOccluding ? opacity * 0.4 : 0.4} metalness={0.9} />
      </mesh>
      <mesh position={[0.61, 0.2, 0]}>
        <boxGeometry args={[0.02, 0.12, 0.35]} />
        <meshStandardMaterial color="#C0C0C0" metalness={0.9} roughness={0.2} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {[-0.12, -0.04, 0.04, 0.12].map((z, i) => (
        <mesh key={`grille-${i}`} position={[0.615, 0.2, z]}>
          <boxGeometry args={[0.01, 0.08, 0.02]} />
          <meshStandardMaterial color="#2a2a2a" metalness={0.8} transparent={isOccluding} opacity={opacity} />
        </mesh>
      ))}
      <mesh position={[0.68, 0.12, 0]} castShadow>
        <boxGeometry args={[0.08, 0.1, 0.62]} />
        <meshStandardMaterial color="#C0C0C0" metalness={0.8} roughness={0.3} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[-0.68, 0.12, 0]} castShadow>
        <boxGeometry args={[0.08, 0.1, 0.62]} />
        <meshStandardMaterial color="#C0C0C0" metalness={0.8} roughness={0.3} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {[[-0.42, 0.12, 0.32], [-0.42, 0.12, -0.32], [0.42, 0.12, 0.32], [0.42, 0.12, -0.32]].map((pos, i) => (
        <group key={`wheel-group-${i}`} position={pos as [number, number, number]}>
          <mesh rotation={[Math.PI / 2, 0, 0]} castShadow>
            <torusGeometry args={[0.1, 0.04, 8, 16]} />
            <meshStandardMaterial color="#1a1a1a" roughness={0.95} />
          </mesh>
          <mesh rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.07, 0.07, 0.03, 12]} />
            <meshStandardMaterial color="#C0C0C0" metalness={0.9} roughness={0.2} />
          </mesh>
          <mesh position={[0, 0, i % 2 === 0 ? 0.02 : -0.02]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.025, 0.025, 0.02, 8]} />
            <meshStandardMaterial color="#808080" metalness={0.8} />
          </mesh>
        </group>
      ))}
      {[0.18, -0.18].map((z, i) => (
        <group key={`headlight-${i}`} position={[0.62, 0.24, z]}>
          <mesh>
            <boxGeometry args={[0.04, 0.08, 0.1]} />
            <meshStandardMaterial color="#303030" metalness={0.5} transparent={isOccluding} opacity={opacity} />
          </mesh>
          <mesh position={[0.02, 0, 0]}>
            <sphereGeometry args={[0.035, 12, 12]} />
            <meshStandardMaterial color="#FFFFEE" emissive="#FFFF88" emissiveIntensity={0.5} transparent opacity={isOccluding ? opacity * 0.9 : 0.9} />
          </mesh>
        </group>
      ))}
      {[0.26, -0.26].map((z, i) => (
        <mesh key={`turn-${i}`} position={[0.62, 0.2, z]}>
          <boxGeometry args={[0.03, 0.04, 0.04]} />
          <meshStandardMaterial color="#FFA500" emissive="#FF8C00" emissiveIntensity={0.2} transparent opacity={isOccluding ? opacity * 0.8 : 0.8} />
        </mesh>
      ))}
      {[0.22, -0.22].map((z, i) => (
        <group key={`taillight-${i}`} position={[-0.66, 0.2, z]}>
          <mesh>
            <boxGeometry args={[0.03, 0.1, 0.12]} />
            <meshStandardMaterial color="#8B0000" transparent={isOccluding} opacity={opacity} />
          </mesh>
          <mesh position={[-0.01, 0.02, 0]}>
            <boxGeometry args={[0.02, 0.04, 0.08]} />
            <meshStandardMaterial color="#FF0000" emissive="#FF0000" emissiveIntensity={0.3} />
          </mesh>
          <mesh position={[-0.01, -0.03, 0]}>
            <boxGeometry args={[0.02, 0.03, 0.08]} />
            <meshStandardMaterial color="#FF4500" emissive="#FF4500" emissiveIntensity={0.2} />
          </mesh>
        </group>
      ))}
      {[0.3, -0.3].map((z, i) => (
        <group key={`mirror-${i}`} position={[0.15, 0.45, z * 1.05]}>
          <mesh rotation={[0, z > 0 ? 0.3 : -0.3, 0]}>
            <boxGeometry args={[0.06, 0.04, 0.02]} />
            <meshStandardMaterial color={carColorDark} metalness={0.6} transparent={isOccluding} opacity={opacity} />
          </mesh>
        </group>
      ))}
      {[[0.05, 0.35, 0.295], [0.05, 0.35, -0.295]].map((pos, i) => (
        <mesh key={`handle-${i}`} position={pos as [number, number, number]}>
          <boxGeometry args={[0.06, 0.015, 0.01]} />
          <meshStandardMaterial color="#C0C0C0" metalness={0.9} transparent={isOccluding} opacity={opacity} />
        </mesh>
      ))}
      <mesh position={[-0.2, 0.75, 0.2]} castShadow>
        <cylinderGeometry args={[0.003, 0.003, 0.4, 6]} />
        <meshStandardMaterial color="#2a2a2a" metalness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0, 0.28, 0.295]}>
        <boxGeometry args={[1.1, 0.02, 0.005]} />
        <meshStandardMaterial color="#C0C0C0" metalness={0.95} roughness={0.1} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0, 0.28, -0.295]}>
        <boxGeometry args={[1.1, 0.02, 0.005]} />
        <meshStandardMaterial color="#C0C0C0" metalness={0.95} roughness={0.1} transparent={isOccluding} opacity={opacity} />
      </mesh>
    </group>
  );
}

export function CarWreckedModel({ isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      <mesh position={[0, 0.06, 0]} rotation={[0.05, 0, 0.03]} castShadow>
        <boxGeometry args={[1.4, 0.08, 0.6]} />
        <meshStandardMaterial color="#3a2a1a" roughness={1} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0.35, 0.15, 0]} rotation={[-0.15, 0.05, 0]} castShadow>
        <boxGeometry args={[0.5, 0.15, 0.55]} />
        <meshStandardMaterial color="#4a3020" roughness={0.95} metalness={0.2} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[-0.1, 0.2, 0.05]} rotation={[0, 0, 0.05]} castShadow>
        <boxGeometry args={[0.6, 0.22, 0.55]} />
        <meshStandardMaterial color="#3d2818" roughness={0.95} metalness={0.1} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[-0.5, 0.25, 0]} rotation={[0.3, 0, -0.1]} castShadow>
        <boxGeometry args={[0.35, 0.15, 0.5]} />
        <meshStandardMaterial color="#4a3828" roughness={0.95} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[-0.05, 0.4, 0.03]} rotation={[0.08, 0.05, -0.1]} castShadow>
        <boxGeometry args={[0.5, 0.18, 0.48]} />
        <meshStandardMaterial color="#3a2515" roughness={0.98} metalness={0.1} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {[[0.2, 0.18, 0.29], [-0.3, 0.22, -0.28], [0.4, 0.12, 0.25]].map((pos, i) => (
        <mesh key={`rust-hole-${i}`} position={pos as [number, number, number]} rotation={[Math.PI / 2, 0, i * 1.2]}>
          <circleGeometry args={[0.05 + i * 0.015, 8]} />
          <meshStandardMaterial color="#1a0a00" roughness={1} side={2} />
        </mesh>
      ))}
      <mesh position={[0.2, 0.42, 0]} rotation={[0, 0, 0.5]}>
        <boxGeometry args={[0.2, 0.02, 0.4]} />
        <meshStandardMaterial color="#2a2a2a" roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {[[-0.1, 0.35, 0.2], [0.05, 0.32, -0.15], [-0.2, 0.38, 0.1]].map((pos, i) => (
        <mesh key={`glass-${i}`} position={pos as [number, number, number]} rotation={[i * 0.5, i * 0.3, i * 0.7]}>
          <boxGeometry args={[0.04, 0.002, 0.03]} />
          <meshStandardMaterial color="#445566" transparent opacity={isOccluding ? opacity * 0.4 : 0.4} metalness={0.9} />
        </mesh>
      ))}
      <group position={[0.42, 0.08, 0.32]}>
        <mesh rotation={[Math.PI / 2, 0, 0]} castShadow>
          <torusGeometry args={[0.1, 0.02, 6, 12]} />
          <meshStandardMaterial color="#0a0a0a" roughness={1} />
        </mesh>
      </group>
      <mesh position={[0.42, 0.08, -0.32]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.02, 0.02, 0.1, 6]} />
        <meshStandardMaterial color="#3a2a1a" roughness={0.9} />
      </mesh>
      <group position={[-0.42, 0.1, 0.3]}>
        <mesh rotation={[Math.PI / 2, 0.2, 0]} castShadow>
          <torusGeometry args={[0.1, 0.035, 6, 10]} />
          <meshStandardMaterial color="#1a1a1a" roughness={1} />
        </mesh>
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.06, 0.06, 0.03, 8]} />
          <meshStandardMaterial color="#5a4a3a" roughness={0.9} />
        </mesh>
      </group>
      <group position={[-0.55, 0.1, -0.5]} rotation={[0.3, 0, 0.5]}>
        <mesh rotation={[Math.PI / 2, 0, 0]} castShadow>
          <torusGeometry args={[0.1, 0.04, 6, 10]} />
          <meshStandardMaterial color="#1a1a1a" roughness={1} />
        </mesh>
      </group>
      <mesh position={[0.6, 0.18, 0.18]} rotation={[0.2, 0.1, 0]}>
        <boxGeometry args={[0.05, 0.06, 0.08]} />
        <meshStandardMaterial color="#2a2a2a" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0.62, 0.2, -0.18]}>
        <sphereGeometry args={[0.03, 8, 8]} />
        <meshStandardMaterial color="#443322" emissive="#332211" emissiveIntensity={0.1} />
      </mesh>
      <mesh position={[0.75, 0.05, 0.15]} rotation={[0.5, 0.3, 0.2]} castShadow>
        <boxGeometry args={[0.15, 0.08, 0.3]} />
        <meshStandardMaterial color="#5a4a3a" roughness={0.95} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {[[0.1, 0.5, 0.2], [-0.2, 0.45, -0.15], [0.3, 0.3, 0]].map((pos, i) => (
        <group key={`vine-car-${i}`} position={pos as [number, number, number]}>
          <mesh rotation={[i * 0.3, i * 0.5, 0]}>
            <cylinderGeometry args={[0.008, 0.012, 0.3 + i * 0.1, 5]} />
            <meshStandardMaterial color="#1a2a1a" roughness={0.9} />
          </mesh>
        </group>
      ))}
      {[[0.2, 0.35, 0.25], [-0.15, 0.25, 0.28], [-0.4, 0.3, -0.2]].map((pos, i) => (
        <mesh key={`moss-${i}`} position={pos as [number, number, number]}>
          <sphereGeometry args={[0.04 + i * 0.01, 6, 6]} />
          <meshStandardMaterial color="#2a3a2a" roughness={1} />
        </mesh>
      ))}
    </group>
  );
}

export function SchoolBusModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const busYellow = '#FFB800';
  const busYellowDark = '#CC9400';
  const busRust = world === 'spectral' ? 0.4 : 0.1;
  const busColor = world === 'spectral' ? '#8B7500' : busYellow;
  const busDamage = world === 'spectral';
  return (
    <group>
      <mesh position={[0, 0.18, 0]} castShadow>
        <boxGeometry args={[3.2, 0.12, 0.9]} />
        <meshStandardMaterial color="#1a1a1a" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0, 0.7, 0]} rotation={busDamage ? [0.02, 0, 0.01] : [0, 0, 0]} castShadow>
        <boxGeometry args={[2.8, 0.85, 0.85]} />
        <meshStandardMaterial color={busColor} roughness={0.5 + busRust} metalness={0.3} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[1.55, 0.48, 0]} rotation={busDamage ? [-0.05, 0, 0] : [0, 0, 0]} castShadow>
        <boxGeometry args={[0.5, 0.48, 0.82]} />
        <meshStandardMaterial color={busColor} roughness={0.5 + busRust} metalness={0.3} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0, 1.18, 0]} rotation={busDamage ? [0.01, 0, -0.02] : [0, 0, 0]} castShadow>
        <boxGeometry args={[2.9, 0.1, 0.88]} />
        <meshStandardMaterial color={busColor} roughness={0.5 + busRust} metalness={0.3} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0, 1.1, 0.43]}>
        <boxGeometry args={[2.8, 0.08, 0.02]} />
        <meshStandardMaterial color="#1a1a1a" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0, 1.1, -0.43]}>
        <boxGeometry args={[2.8, 0.08, 0.02]} />
        <meshStandardMaterial color="#1a1a1a" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {[-1.0, -0.5, 0, 0.5, 1.0].map((x, i) => (
        <mesh key={`window-l-${i}`} position={[x, 0.8, 0.44]} rotation={busDamage && i === 2 ? [0, 0, 0.05] : [0, 0, 0]}>
          <boxGeometry args={[0.38, 0.4, 0.02]} />
          <meshStandardMaterial
            color={busDamage && i === 2 ? "#3a3a3a" : "#87CEEB"}
            transparent
            opacity={isOccluding ? opacity * (busDamage && i === 2 ? 0.3 : 0.6) : (busDamage && i === 2 ? 0.3 : 0.6)}
            metalness={0.9}
            roughness={0.1}
          />
        </mesh>
      ))}
      {[-1.0, -0.5, 0, 0.5, 1.0].map((x, i) => (
        <mesh key={`window-r-${i}`} position={[x, 0.8, -0.44]}>
          <boxGeometry args={[0.38, 0.4, 0.02]} />
          <meshStandardMaterial
            color={busDamage && i === 3 ? "#2a2a2a" : "#87CEEB"}
            transparent
            opacity={isOccluding ? opacity * (busDamage && i === 3 ? 0.2 : 0.6) : (busDamage && i === 3 ? 0.2 : 0.6)}
            metalness={0.9}
            roughness={0.1}
          />
        </mesh>
      ))}
      <mesh position={[1.4, 0.75, 0]} rotation={[0, 0, busDamage ? 0.15 : 0.1]}>
        <boxGeometry args={[0.35, 0.5, 0.7]} />
        <meshStandardMaterial color="#87CEEB" transparent opacity={isOccluding ? opacity * (busDamage ? 0.4 : 0.6) : (busDamage ? 0.4 : 0.6)} metalness={0.9} roughness={0.1} />
      </mesh>
      <mesh position={[-1.42, 0.75, 0]}>
        <boxGeometry args={[0.02, 0.4, 0.5]} />
        <meshStandardMaterial color="#87CEEB" transparent opacity={isOccluding ? opacity * 0.5 : 0.5} metalness={0.9} roughness={0.1} />
      </mesh>
      <mesh position={[1.81, 0.4, 0]}>
        <boxGeometry args={[0.02, 0.25, 0.5]} />
        <meshStandardMaterial color="#1a1a1a" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {[-0.15, -0.05, 0.05, 0.15].map((z, i) => (
        <mesh key={`bus-grille-${i}`} position={[1.82, 0.4, z]}>
          <boxGeometry args={[0.01, 0.2, 0.03]} />
          <meshStandardMaterial color="#3a3a3a" metalness={0.7} transparent={isOccluding} opacity={opacity} />
        </mesh>
      ))}
      <mesh position={[1.82, 0.38, 0.28]}>
        <cylinderGeometry args={[0.08, 0.08, 0.03, 12]} />
        <meshStandardMaterial color="#FFFACD" emissive={world === 'real' ? "#FFFF00" : "#000000"} emissiveIntensity={0.3} metalness={0.8} />
      </mesh>
      <mesh position={[1.82, 0.38, -0.28]}>
        <cylinderGeometry args={[0.08, 0.08, 0.03, 12]} />
        <meshStandardMaterial color="#FFFACD" emissive={world === 'real' ? "#FFFF00" : "#000000"} emissiveIntensity={0.3} metalness={0.8} />
      </mesh>
      {[[-0.4, 1.22, 0.35], [0.4, 1.22, 0.35], [-0.4, 1.22, -0.35], [0.4, 1.22, -0.35]].map((pos, i) => (
        <mesh key={`blinker-${i}`} position={pos as [number, number, number]}>
          <boxGeometry args={[0.12, 0.04, 0.08]} />
          <meshStandardMaterial color="#FF6B00" emissive="#FF4500" emissiveIntensity={0.2} />
        </mesh>
      ))}
      <mesh position={[1.7, 1.0, 0.35]}>
        <sphereGeometry args={[0.06, 8, 8]} />
        <meshStandardMaterial color="#FF0000" emissive="#FF0000" emissiveIntensity={world === 'real' ? 0.3 : 0} />
      </mesh>
      <mesh position={[1.7, 1.0, -0.35]}>
        <sphereGeometry args={[0.06, 8, 8]} />
        <meshStandardMaterial color="#FF0000" emissive="#FF0000" emissiveIntensity={world === 'real' ? 0.3 : 0} />
      </mesh>
      <group position={[1.2, 0.7, 0.48]} rotation={[0, 0, busDamage ? 0.3 : 0]}>
        <mesh>
          <boxGeometry args={[0.02, 0.25, 0.25]} />
          <meshStandardMaterial color="#FF0000" roughness={0.7} transparent={isOccluding} opacity={opacity} />
        </mesh>
        <mesh position={[0.012, 0, 0]}>
          <boxGeometry args={[0.005, 0.08, 0.15]} />
          <meshStandardMaterial color="#FFFFFF" />
        </mesh>
      </group>
      <mesh position={[1.85, 0.22, 0]} castShadow>
        <boxGeometry args={[0.1, 0.12, 0.9]} />
        <meshStandardMaterial color="#2a2a2a" roughness={0.8} metalness={0.5} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[-1.6, 0.22, 0]} castShadow>
        <boxGeometry args={[0.1, 0.12, 0.9]} />
        <meshStandardMaterial color="#2a2a2a" roughness={0.8} metalness={0.5} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[-1.44, 0.4, 0.35]}>
        <boxGeometry args={[0.02, 0.15, 0.1]} />
        <meshStandardMaterial color="#FF0000" emissive="#660000" emissiveIntensity={0.2} />
      </mesh>
      <mesh position={[-1.44, 0.4, -0.35]}>
        <boxGeometry args={[0.02, 0.15, 0.1]} />
        <meshStandardMaterial color="#FF0000" emissive="#660000" emissiveIntensity={0.2} />
      </mesh>
      {[[-1.1, 0.18, 0.48], [-1.1, 0.18, -0.48], [1.2, 0.18, 0.48], [1.2, 0.18, -0.48]].map((pos, i) => (
        <group key={`bus-wheel-${i}`} position={pos as [number, number, number]}>
          <mesh rotation={[Math.PI / 2, 0, 0]} castShadow>
            <torusGeometry args={[0.16, 0.06, 12, 20]} />
            <meshStandardMaterial color="#1a1a1a" roughness={0.95} />
          </mesh>
          <mesh rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.1, 0.1, 0.05, 16]} />
            <meshStandardMaterial color="#4a4a4a" metalness={0.8} roughness={0.3} />
          </mesh>
          {[0, 1, 2, 3, 4].map((j) => (
            <mesh key={`lug-${j}`} position={[Math.cos(j * Math.PI * 2 / 5) * 0.06, Math.sin(j * Math.PI * 2 / 5) * 0.06, i % 2 === 0 ? 0.03 : -0.03]} rotation={[Math.PI / 2, 0, 0]}>
              <cylinderGeometry args={[0.015, 0.015, 0.02, 6]} />
              <meshStandardMaterial color="#3a3a3a" metalness={0.9} />
            </mesh>
          ))}
        </group>
      ))}
      <mesh position={[1.0, 0.55, 0.44]} rotation={busDamage ? [0, 0.4, 0] : [0, 0, 0]}>
        <boxGeometry args={[0.35, 0.7, 0.03]} />
        <meshStandardMaterial color={busYellowDark} roughness={0.6} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <group position={[1.5, 0.9, 0.55]}>
        <mesh rotation={[0, 0.3, 0]}>
          <boxGeometry args={[0.03, 0.15, 0.08]} />
          <meshStandardMaterial color="#1a1a1a" transparent={isOccluding} opacity={opacity} />
        </mesh>
        <mesh position={[0.02, 0, 0.06]} rotation={[0, 0.3, 0]}>
          <boxGeometry args={[0.01, 0.12, 0.1]} />
          <meshStandardMaterial color="#87CEEB" metalness={0.95} roughness={0.1} />
        </mesh>
      </group>
      <group position={[1.5, 0.9, -0.55]}>
        <mesh rotation={[0, -0.3, 0]}>
          <boxGeometry args={[0.03, 0.15, 0.08]} />
          <meshStandardMaterial color="#1a1a1a" transparent={isOccluding} opacity={opacity} />
        </mesh>
        <mesh position={[0.02, 0, -0.06]} rotation={[0, -0.3, 0]}>
          <boxGeometry args={[0.01, 0.12, 0.1]} />
          <meshStandardMaterial color="#87CEEB" metalness={0.95} roughness={0.1} />
        </mesh>
      </group>
      <mesh position={[0, 1.0, 0.435]}>
        <boxGeometry args={[1.2, 0.12, 0.01]} />
        <meshStandardMaterial color="#1a1a1a" transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0, 1.0, -0.435]}>
        <boxGeometry args={[1.2, 0.12, 0.01]} />
        <meshStandardMaterial color="#1a1a1a" transparent={isOccluding} opacity={opacity} />
      </mesh>
      {busDamage && (
        <>
          <mesh position={[0.3, 0.75, 0.44]} rotation={[0, 0, 0.2]}>
            <sphereGeometry args={[0.08, 8, 8, 0, Math.PI]} />
            <meshStandardMaterial color="#6B5A00" roughness={0.95} transparent={isOccluding} opacity={opacity} />
          </mesh>
          <mesh position={[-0.7, 0.6, -0.44]}>
            <sphereGeometry args={[0.1, 8, 8, 0, Math.PI]} />
            <meshStandardMaterial color="#5a4a00" roughness={0.95} transparent={isOccluding} opacity={opacity} />
          </mesh>
          {[[0.8, 0.4, 0.43], [-0.5, 0.35, 0.43], [1.0, 0.9, -0.43], [-1.0, 0.5, -0.43]].map((pos, i) => (
            <mesh key={`bus-rust-${i}`} position={pos as [number, number, number]}>
              <circleGeometry args={[0.06 + i * 0.015, 8]} />
              <meshStandardMaterial color="#4a2a00" roughness={1} side={2} />
            </mesh>
          ))}
          <mesh position={[0, 0.8, 0.445]}>
            <boxGeometry args={[0.35, 0.005, 0.002]} />
            <meshStandardMaterial color="#FFFFFF" transparent opacity={0.4} />
          </mesh>
          <mesh position={[0, 0.8, 0.445]} rotation={[0, 0, 0.6]}>
            <boxGeometry args={[0.2, 0.005, 0.002]} />
            <meshStandardMaterial color="#FFFFFF" transparent opacity={0.4} />
          </mesh>
          {[[0.5, 1.15, 0], [-0.8, 1.0, 0.3], [1.3, 0.5, -0.2]].map((pos, i) => (
            <mesh key={`bus-vine-${i}`} position={pos as [number, number, number]} rotation={[i * 0.4, i * 0.6, i * 0.3]}>
              <cylinderGeometry args={[0.01, 0.015, 0.4 + i * 0.15, 5]} />
              <meshStandardMaterial color="#1a2a1a" roughness={0.9} />
            </mesh>
          ))}
          {[[1.4, 0.5, 0.3], [-1.2, 0.3, -0.25], [0.2, 1.2, 0]].map((pos, i) => (
            <mesh key={`bus-moss-${i}`} position={pos as [number, number, number]}>
              <sphereGeometry args={[0.05 + i * 0.01, 6, 6]} />
              <meshStandardMaterial color="#2a3a2a" roughness={1} />
            </mesh>
          ))}
          <mesh position={[1.2, 0.12, 0.48]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.16, 0.03, 8, 16]} />
            <meshStandardMaterial color="#0a0a0a" roughness={1} />
          </mesh>
        </>
      )}
      <mesh position={[-1.4, 0.15, 0.35]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.03, 0.035, 0.3, 8]} />
        <meshStandardMaterial color="#2a2a2a" roughness={0.8} metalness={0.6} />
      </mesh>
    </group>
  );
}

// ===== HAWKINS SCHOOL =====

export function HawkinsSchoolModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const isSpectral = world === 'spectral';
  const brickColor = isSpectral ? '#3a2a2a' : '#8B4513';
  const roofColor = isSpectral ? '#1a1a2a' : '#2F2F2F';
  const windowColor = isSpectral ? '#1a1a3a' : '#87CEEB';

  return (
    <group>
      {/* Bâtiment principal */}
      <mesh position={[0, 1, 0]} castShadow>
        <boxGeometry args={[4, 2, 2.5]} />
        <meshStandardMaterial color={brickColor} roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>

      {/* Toit */}
      <mesh position={[0, 2.3, 0]} castShadow>
        <boxGeometry args={[4.2, 0.4, 2.7]} />
        <meshStandardMaterial color={roofColor} roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>

      {/* Entrée centrale */}
      <mesh position={[0, 0.6, 1.26]} castShadow>
        <boxGeometry args={[1, 1.2, 0.1]} />
        <meshStandardMaterial color="#5a4a3a" roughness={0.9} />
      </mesh>

      {/* Porte */}
      <mesh position={[0, 0.5, 1.32]}>
        <boxGeometry args={[0.5, 0.9, 0.05]} />
        <meshStandardMaterial color="#4a3020" roughness={0.9} />
      </mesh>

      {/* Fenêtres - rangée du haut */}
      {[-1.4, -0.7, 0.7, 1.4].map((x, i) => (
        <mesh key={`window-top-${i}`} position={[x, 1.4, 1.26]}>
          <boxGeometry args={[0.35, 0.4, 0.05]} />
          <meshStandardMaterial color={windowColor} transparent opacity={0.8} />
        </mesh>
      ))}

      {/* Fenêtres - rangée du bas */}
      {[-1.4, 1.4].map((x, i) => (
        <mesh key={`window-bot-${i}`} position={[x, 0.6, 1.26]}>
          <boxGeometry args={[0.35, 0.5, 0.05]} />
          <meshStandardMaterial color={windowColor} transparent opacity={0.8} />
        </mesh>
      ))}

      {/* Panneau "HAWKINS MIDDLE SCHOOL" */}
      <mesh position={[0, 2, 1.26]}>
        <boxGeometry args={[2.5, 0.3, 0.05]} />
        <meshStandardMaterial color="#2a2a2a" />
      </mesh>

      {/* Drapeau américain */}
      <mesh position={[1.5, 2.8, 0]} castShadow>
        <cylinderGeometry args={[0.02, 0.02, 1, 6]} />
        <meshStandardMaterial color="#888888" metalness={0.8} />
      </mesh>
      <mesh position={[1.7, 3.1, 0]}>
        <boxGeometry args={[0.4, 0.25, 0.02]} />
        <meshStandardMaterial color="#bf0a30" />
      </mesh>

      {/* Escaliers d'entrée */}
      <mesh position={[0, 0.1, 1.6]} castShadow>
        <boxGeometry args={[1.2, 0.2, 0.5]} />
        <meshStandardMaterial color="#666666" roughness={0.9} />
      </mesh>
    </group>
  );
}

// ===== NPC DUSTIN =====

export function NPCDustinModel({ world, hovered }: ModelProps) {
  const isSpectral = world === 'spectral';
  const skinColor = isSpectral ? '#c8b8a8' : '#ffe4c4';
  const hairColor = isSpectral ? '#4a3a2a' : '#5a4030';
  const shirtColor = isSpectral ? '#2a4a6a' : '#4a7ab0';
  const pantsColor = isSpectral ? '#2a2a4a' : '#3a3a5a';

  return (
    <group>
      {/* Hitbox pour clic */}
      <mesh position={[0, 0.5, 0]}>
        <boxGeometry args={[1, 1.2, 1]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>

      {/* Jambes */}
      <mesh position={[-0.08, 0.2, 0]} castShadow>
        <cylinderGeometry args={[0.06, 0.07, 0.35, 8]} />
        <meshStandardMaterial color={pantsColor} />
      </mesh>
      <mesh position={[0.08, 0.2, 0]} castShadow>
        <cylinderGeometry args={[0.06, 0.07, 0.35, 8]} />
        <meshStandardMaterial color={pantsColor} />
      </mesh>

      {/* Chaussures */}
      <mesh position={[-0.08, 0.03, 0.02]} castShadow>
        <boxGeometry args={[0.1, 0.06, 0.15]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      <mesh position={[0.08, 0.03, 0.02]} castShadow>
        <boxGeometry args={[0.1, 0.06, 0.15]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>

      {/* Torse */}
      <mesh position={[0, 0.55, 0]} castShadow>
        <boxGeometry args={[0.3, 0.35, 0.2]} />
        <meshStandardMaterial color={shirtColor} />
      </mesh>

      {/* Bras */}
      <mesh position={[-0.2, 0.5, 0]} rotation={[0, 0, 0.2]} castShadow>
        <cylinderGeometry args={[0.04, 0.05, 0.25, 8]} />
        <meshStandardMaterial color={shirtColor} />
      </mesh>
      <mesh position={[0.2, 0.5, 0]} rotation={[0, 0, -0.2]} castShadow>
        <cylinderGeometry args={[0.04, 0.05, 0.25, 8]} />
        <meshStandardMaterial color={shirtColor} />
      </mesh>

      {/* Mains */}
      <mesh position={[-0.25, 0.38, 0]} castShadow>
        <sphereGeometry args={[0.035, 8, 8]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>
      <mesh position={[0.25, 0.38, 0]} castShadow>
        <sphereGeometry args={[0.035, 8, 8]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>

      {/* Tête */}
      <mesh position={[0, 0.82, 0]} castShadow>
        <sphereGeometry args={[0.12, 12, 12]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>

      {/* Cheveux bouclés caractéristiques de Dustin */}
      <mesh position={[0, 0.92, 0]} castShadow>
        <sphereGeometry args={[0.14, 12, 12, 0, Math.PI * 2, 0, Math.PI / 1.8]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>
      {/* Boucles supplémentaires */}
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const angle = (i / 6) * Math.PI * 2;
        return (
          <mesh key={`curl-${i}`} position={[
            Math.cos(angle) * 0.12,
            0.9,
            Math.sin(angle) * 0.12
          ]} castShadow>
            <sphereGeometry args={[0.04, 6, 6]} />
            <meshStandardMaterial color={hairColor} />
          </mesh>
        );
      })}

      {/* Casquette */}
      <mesh position={[0, 0.98, 0]} castShadow>
        <cylinderGeometry args={[0.14, 0.13, 0.06, 12]} />
        <meshStandardMaterial color="#dc143c" />
      </mesh>
      {/* Visière plus grande */}
      <mesh position={[0, 0.95, 0.16]} rotation={[0.4, 0, 0]} castShadow>
        <boxGeometry args={[0.16, 0.02, 0.14]} />
        <meshStandardMaterial color="#dc143c" />
      </mesh>

      {/* Yeux */}
      <mesh position={[-0.04, 0.84, 0.1]}>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      <mesh position={[0.04, 0.84, 0.1]}>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      <mesh position={[-0.04, 0.84, 0.115]}>
        <sphereGeometry args={[0.01, 6, 6]} />
        <meshStandardMaterial color="#4a3020" />
      </mesh>
      <mesh position={[0.04, 0.84, 0.115]}>
        <sphereGeometry args={[0.01, 6, 6]} />
        <meshStandardMaterial color="#4a3020" />
      </mesh>

      {/* Sourire caractéristique */}
      <mesh position={[0, 0.78, 0.11]} rotation={[0, 0, 0]}>
        <torusGeometry args={[0.03, 0.008, 6, 8, Math.PI]} />
        <meshStandardMaterial color="#cc8866" />
      </mesh>

      {/* Hover effect */}
      {hovered && (
        <pointLight position={[0, 0.8, 0.3]} intensity={0.5} color="#ffffff" distance={2} />
      )}
    </group>
  );
}

// ===== NPC ERICA =====

export function NPCEricaModel({ world, hovered }: ModelProps) {
  const isSpectral = world === 'spectral';
  const skinColor = isSpectral ? '#4a3020' : '#5c3d2e';
  const hairColor = isSpectral ? '#1a1a2a' : '#1a1a1a';
  const shirtColor = isSpectral ? '#5a2a4a' : '#ff69b4';
  const pantsColor = isSpectral ? '#2a2a4a' : '#4169e1';

  return (
    <group>
      {/* Hitbox pour clic */}
      <mesh position={[0, 0.45, 0]}>
        <boxGeometry args={[0.9, 1.1, 0.9]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>

      {/* Jambes - Erica est plus petite */}
      <mesh position={[-0.06, 0.18, 0]} castShadow>
        <cylinderGeometry args={[0.05, 0.06, 0.3, 8]} />
        <meshStandardMaterial color={pantsColor} />
      </mesh>
      <mesh position={[0.06, 0.18, 0]} castShadow>
        <cylinderGeometry args={[0.05, 0.06, 0.3, 8]} />
        <meshStandardMaterial color={pantsColor} />
      </mesh>

      {/* Chaussures rose vif */}
      <mesh position={[-0.06, 0.03, 0.02]} castShadow>
        <boxGeometry args={[0.08, 0.05, 0.12]} />
        <meshStandardMaterial color="#ff1493" />
      </mesh>
      <mesh position={[0.06, 0.03, 0.02]} castShadow>
        <boxGeometry args={[0.08, 0.05, 0.12]} />
        <meshStandardMaterial color="#ff1493" />
      </mesh>

      {/* Torse */}
      <mesh position={[0, 0.48, 0]} castShadow>
        <boxGeometry args={[0.25, 0.3, 0.18]} />
        <meshStandardMaterial color={shirtColor} />
      </mesh>

      {/* Bras */}
      <mesh position={[-0.17, 0.45, 0]} rotation={[0, 0, 0.3]} castShadow>
        <cylinderGeometry args={[0.035, 0.04, 0.22, 8]} />
        <meshStandardMaterial color={shirtColor} />
      </mesh>
      <mesh position={[0.17, 0.45, 0]} rotation={[0, 0, -0.3]} castShadow>
        <cylinderGeometry args={[0.035, 0.04, 0.22, 8]} />
        <meshStandardMaterial color={shirtColor} />
      </mesh>

      {/* Mains */}
      <mesh position={[-0.22, 0.35, 0]} castShadow>
        <sphereGeometry args={[0.03, 8, 8]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>
      <mesh position={[0.22, 0.35, 0]} castShadow>
        <sphereGeometry args={[0.03, 8, 8]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>

      {/* Tête */}
      <mesh position={[0, 0.72, 0]} castShadow>
        <sphereGeometry args={[0.1, 12, 12]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>

      {/* Cheveux avec couettes */}
      <mesh position={[0, 0.78, -0.02]} castShadow>
        <sphereGeometry args={[0.11, 12, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>
      {/* Couettes */}
      <mesh position={[-0.12, 0.75, 0]} castShadow>
        <sphereGeometry args={[0.05, 8, 8]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>
      <mesh position={[0.12, 0.75, 0]} castShadow>
        <sphereGeometry args={[0.05, 8, 8]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>
      <mesh position={[-0.14, 0.68, 0]} castShadow>
        <capsuleGeometry args={[0.03, 0.08, 6, 6]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>
      <mesh position={[0.14, 0.68, 0]} castShadow>
        <capsuleGeometry args={[0.03, 0.08, 6, 6]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>

      {/* Nœuds dans les cheveux */}
      <mesh position={[-0.12, 0.77, 0]} castShadow>
        <sphereGeometry args={[0.02, 6, 6]} />
        <meshStandardMaterial color="#ff69b4" />
      </mesh>
      <mesh position={[0.12, 0.77, 0]} castShadow>
        <sphereGeometry args={[0.02, 6, 6]} />
        <meshStandardMaterial color="#ff69b4" />
      </mesh>

      {/* Yeux expressifs */}
      <mesh position={[-0.035, 0.74, 0.08]}>
        <sphereGeometry args={[0.018, 8, 8]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      <mesh position={[0.035, 0.74, 0.08]}>
        <sphereGeometry args={[0.018, 8, 8]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      <mesh position={[-0.035, 0.74, 0.095]}>
        <sphereGeometry args={[0.009, 6, 6]} />
        <meshStandardMaterial color="#2a1a0a" />
      </mesh>
      <mesh position={[0.035, 0.74, 0.095]}>
        <sphereGeometry args={[0.009, 6, 6]} />
        <meshStandardMaterial color="#2a1a0a" />
      </mesh>

      {/* Expression déterminée / sass */}
      <mesh position={[-0.035, 0.77, 0.08]} rotation={[0, 0, -0.2]}>
        <boxGeometry args={[0.025, 0.008, 0.005]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      <mesh position={[0.035, 0.77, 0.08]} rotation={[0, 0, 0.2]}>
        <boxGeometry args={[0.025, 0.008, 0.005]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>

      {/* Bouche confiante */}
      <mesh position={[0, 0.69, 0.09]}>
        <boxGeometry args={[0.03, 0.01, 0.01]} />
        <meshStandardMaterial color="#8b4040" />
      </mesh>

      {/* Hover effect */}
      {hovered && (
        <pointLight position={[0, 0.7, 0.3]} intensity={0.5} color="#ff69b4" distance={2} />
      )}
    </group>
  );
}

// ===== DIMENSIONAL RIFT / FAILLE =====

export function DimensionalRiftModel({ world, hovered, properties }: ModelProps) {
  const isOpen = properties?.isOpen as boolean;
  const riftColor = '#ff0000';
  const secondaryColor = '#ff4444';
  const glowIntensity = hovered ? 2.5 : 1.5;

  return (
    <group>
      {/* Hitbox pour clic */}
      <mesh position={[0, 1, 0]}>
        <boxGeometry args={[2, 2.5, 0.5]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>

      {/* Cadre de la faille - bords irréguliers */}
      <mesh position={[0, 1.2, 0]} castShadow>
        <torusGeometry args={[0.9, 0.12, 6, 8]} />
        <meshStandardMaterial
          color="#1a0a0a"
          roughness={0.9}
          emissive={riftColor}
          emissiveIntensity={0.2}
        />
      </mesh>

      {/* Surface de la faille - rouge sang pulsant */}
      {isOpen && (
        <>
          <mesh position={[0, 1.2, 0]}>
            <circleGeometry args={[0.85, 16]} />
            <meshStandardMaterial
              color={riftColor}
              emissive={riftColor}
              emissiveIntensity={glowIntensity}
              transparent
              opacity={0.8}
              side={2}
            />
          </mesh>

          {/* Effet de déchirure - lignes irrégulières */}
          {[0, 1, 2, 3, 4, 5].map((i) => {
            const angle = (i / 6) * Math.PI * 2 + 0.3;
            const len = 0.3 + (i % 2) * 0.2;
            return (
              <mesh key={`tear-${i}`} position={[
                Math.cos(angle) * 0.4,
                1.2 + Math.sin(i * 1.5) * 0.2,
                0.05
              ]} rotation={[0, 0, angle]}>
                <boxGeometry args={[0.03, len, 0.02]} />
                <meshStandardMaterial
                  color={secondaryColor}
                  emissive={secondaryColor}
                  emissiveIntensity={glowIntensity * 0.8}
                />
              </mesh>
            );
          })}

          {/* Particules aspirées */}
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
            const angle = (i / 8) * Math.PI * 2;
            const radius = 0.6 + (i % 3) * 0.15;
            return (
              <mesh key={`particle-${i}`} position={[
                Math.cos(angle) * radius,
                1.2 + Math.sin(i * 2) * 0.3,
                0.1
              ]}>
                <sphereGeometry args={[0.03, 6, 6]} />
                <meshStandardMaterial
                  color={riftColor}
                  emissive={riftColor}
                  emissiveIntensity={glowIntensity}
                  transparent
                  opacity={0.7}
                />
              </mesh>
            );
          })}

          {/* Lumière centrale */}
          <pointLight
            position={[0, 1.2, 0.5]}
            intensity={glowIntensity * 3}
            color={riftColor}
            distance={5}
          />
        </>
      )}

      {/* Faille fermée - cicatrice */}
      {!isOpen && (
        <>
          <mesh position={[0, 1.2, 0]}>
            <boxGeometry args={[0.08, 1.5, 0.02]} />
            <meshStandardMaterial
              color="#2a0a0a"
              emissive={riftColor}
              emissiveIntensity={0.3}
            />
          </mesh>
          {/* Petites craquelures */}
          {[-0.3, 0.3].map((offset, i) => (
            <mesh key={`crack-${i}`} position={[offset * 0.3, 1.2 + offset * 0.2, 0]} rotation={[0, 0, offset]}>
              <boxGeometry args={[0.03, 0.4, 0.01]} />
              <meshStandardMaterial
                color="#2a0a0a"
                emissive={riftColor}
                emissiveIntensity={0.2}
              />
            </mesh>
          ))}
        </>
      )}

      {/* Base au sol - corruption */}
      <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[1.2, 16]} />
        <meshStandardMaterial
          color="#1a0a0a"
          emissive={riftColor}
          emissiveIntensity={isOpen ? 0.3 : 0.1}
          transparent
          opacity={0.6}
        />
      </mesh>

      {/* Veines de corruption au sol */}
      {[0, 1, 2, 3].map((i) => {
        const angle = (i / 4) * Math.PI * 2;
        return (
          <mesh key={`vein-${i}`} position={[
            Math.cos(angle) * 0.8,
            0.03,
            Math.sin(angle) * 0.8
          ]} rotation={[-Math.PI / 2, 0, angle]}>
            <boxGeometry args={[0.06, 0.6, 0.02]} />
            <meshStandardMaterial
              color="#2a0a0a"
              emissive={riftColor}
              emissiveIntensity={isOpen ? 0.4 : 0.1}
            />
          </mesh>
        );
      })}
    </group>
  );
}

// ===== DEAD TREE MODEL =====

export function DeadTreeModel({ isOccluding, opacity = 1 }: ModelProps) {
  const trunkColor = '#2a1a1a';
  const branchColor = '#1a1010';

  return (
    <group>
      {/* Tronc principal */}
      <mesh position={[0, 0.8, 0]} castShadow>
        <cylinderGeometry args={[0.08, 0.15, 1.6, 6]} />
        <meshStandardMaterial color={trunkColor} roughness={0.95} transparent={isOccluding} opacity={opacity} />
      </mesh>

      {/* Branches mortes */}
      <mesh position={[-0.2, 1.3, 0]} rotation={[0, 0, 0.8]} castShadow>
        <cylinderGeometry args={[0.02, 0.04, 0.6, 5]} />
        <meshStandardMaterial color={branchColor} roughness={0.95} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0.15, 1.1, 0.1]} rotation={[0.2, 0, -0.6]} castShadow>
        <cylinderGeometry args={[0.02, 0.035, 0.5, 5]} />
        <meshStandardMaterial color={branchColor} roughness={0.95} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0.1, 0.9, -0.1]} rotation={[-0.3, 0, -0.4]} castShadow>
        <cylinderGeometry args={[0.015, 0.03, 0.4, 5]} />
        <meshStandardMaterial color={branchColor} roughness={0.95} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[-0.1, 1.5, 0]} rotation={[0.1, 0, 0.3]} castShadow>
        <cylinderGeometry args={[0.01, 0.02, 0.3, 4]} />
        <meshStandardMaterial color={branchColor} roughness={0.95} transparent={isOccluding} opacity={opacity} />
      </mesh>

      {/* Racines exposées */}
      <mesh position={[-0.15, 0.05, 0.1]} rotation={[0.5, 0, 0.3]} castShadow>
        <cylinderGeometry args={[0.02, 0.04, 0.3, 4]} />
        <meshStandardMaterial color={trunkColor} roughness={0.95} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0.12, 0.05, -0.08]} rotation={[-0.4, 0, -0.2]} castShadow>
        <cylinderGeometry args={[0.015, 0.03, 0.25, 4]} />
        <meshStandardMaterial color={trunkColor} roughness={0.95} transparent={isOccluding} opacity={opacity} />
      </mesh>
    </group>
  );
}

// ===== OAK TREE MODEL =====

export function OakTreeModel({ isOccluding, opacity = 1 }: ModelProps) {
  const trunkColor = '#5a4030';
  const leafColor = '#228b22';

  return (
    <group>
      {/* Tronc */}
      <mesh position={[0, 0.6, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.15, 1.2, 8]} />
        <meshStandardMaterial color={trunkColor} roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>

      {/* Feuillage - plusieurs sphères pour un effet dense */}
      <mesh position={[0, 1.4, 0]} castShadow>
        <sphereGeometry args={[0.5, 8, 8]} />
        <meshStandardMaterial color={leafColor} roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[-0.25, 1.2, 0.2]} castShadow>
        <sphereGeometry args={[0.35, 8, 8]} />
        <meshStandardMaterial color={leafColor} roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0.2, 1.3, -0.15]} castShadow>
        <sphereGeometry args={[0.3, 8, 8]} />
        <meshStandardMaterial color={leafColor} roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0, 1.6, 0.1]} castShadow>
        <sphereGeometry args={[0.25, 8, 8]} />
        <meshStandardMaterial color={leafColor} roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
    </group>
  );
}

// ===== UPSIDE DOWN BUILDING =====

export function UpsideDownBuildingModel({ isOccluding, opacity = 1 }: ModelProps) {
  const buildingColor = '#1a1a2a';
  const vineColor = '#2d1f3d';

  return (
    <group>
      {/* Structure principale - bâtiment déformé */}
      <mesh position={[0, 1.5, 0]} castShadow>
        <boxGeometry args={[2, 3, 2]} />
        <meshStandardMaterial
          color={buildingColor}
          roughness={0.95}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>

      {/* Fenêtres brisées */}
      {[[-0.5, 2, 1.01], [0.5, 2, 1.01], [-0.5, 1, 1.01], [0.5, 1, 1.01]].map((pos, i) => (
        <mesh key={`window-${i}`} position={pos as [number, number, number]}>
          <boxGeometry args={[0.4, 0.5, 0.02]} />
          <meshStandardMaterial
            color="#0a0a1a"
            emissive="#ff0000"
            emissiveIntensity={0.1}
          />
        </mesh>
      ))}

      {/* Lianes qui recouvrent */}
      {[0, 1, 2, 3].map((i) => {
        const x = (i % 2) * 1.5 - 0.75;
        const z = Math.floor(i / 2) * 1.5 - 0.75;
        return (
          <mesh key={`vine-${i}`} position={[x, 1.5, z]} castShadow>
            <cylinderGeometry args={[0.03, 0.05, 3, 5]} />
            <meshStandardMaterial color={vineColor} roughness={0.9} transparent={isOccluding} opacity={opacity} />
          </mesh>
        );
      })}

      {/* Spores flottantes */}
      {[0, 1, 2].map((i) => (
        <mesh key={`spore-${i}`} position={[(i - 1) * 0.5, 2.5 + i * 0.3, 0.5]}>
          <sphereGeometry args={[0.05, 6, 6]} />
          <meshStandardMaterial
            color="#5a3a7a"
            emissive="#8844aa"
            emissiveIntensity={0.5}
            transparent
            opacity={0.6}
          />
        </mesh>
      ))}
    </group>
  );
}

// ===== BIKE RACK =====

// Simple parked bike component for the rack
function ParkedBike({ color, position }: { color: string; position: [number, number, number] }) {
  return (
    <group position={position} rotation={[0, Math.PI / 2, 0]} scale={[0.6, 0.6, 0.6]}>
      {/* Frame */}
      <mesh position={[0, 0.2, 0]} rotation={[0, 0, 0.3]} castShadow>
        <cylinderGeometry args={[0.02, 0.02, 0.4, 6]} />
        <meshStandardMaterial color={color} metalness={0.6} roughness={0.3} />
      </mesh>
      {/* Fork */}
      <mesh position={[-0.1, 0.25, 0]} rotation={[0, 0, -0.4]} castShadow>
        <cylinderGeometry args={[0.015, 0.015, 0.2, 6]} />
        <meshStandardMaterial color={color} metalness={0.6} roughness={0.3} />
      </mesh>
      {/* Front wheel */}
      <group position={[-0.18, 0.12, 0]}>
        <mesh rotation={[Math.PI / 2, 0, 0]} castShadow>
          <torusGeometry args={[0.1, 0.015, 8, 16]} />
          <meshStandardMaterial color="#333333" roughness={0.8} />
        </mesh>
      </group>
      {/* Back wheel */}
      <group position={[0.18, 0.12, 0]}>
        <mesh rotation={[Math.PI / 2, 0, 0]} castShadow>
          <torusGeometry args={[0.1, 0.015, 8, 16]} />
          <meshStandardMaterial color="#333333" roughness={0.8} />
        </mesh>
      </group>
      {/* Seat */}
      <mesh position={[0.12, 0.3, 0]} castShadow>
        <boxGeometry args={[0.08, 0.02, 0.05]} />
        <meshStandardMaterial color="#2F1810" roughness={0.9} />
      </mesh>
    </group>
  );
}

export function BikeRackModel({ hovered }: ModelProps) {
  // Check which bikes have been deposited
  const solvedRiddles = useGameStore((state) => state.solvedRiddles);
  const blueDeposited = solvedRiddles.has('bike_blue_deposited');
  const yellowDeposited = solvedRiddles.has('bike_yellow_deposited');

  return (
    <group>
      {/* Base frame - horizontal bars */}
      <mesh position={[0, 0.1, 0]} castShadow>
        <boxGeometry args={[1.5, 0.05, 0.05]} />
        <meshStandardMaterial color={hovered ? '#6a6a6a' : '#4a4a4a'} metalness={0.8} roughness={0.3} />
      </mesh>
      <mesh position={[0, 0.3, 0]} castShadow>
        <boxGeometry args={[1.5, 0.05, 0.05]} />
        <meshStandardMaterial color={hovered ? '#6a6a6a' : '#4a4a4a'} metalness={0.8} roughness={0.3} />
      </mesh>
      {/* Vertical supports at ends */}
      <mesh position={[-0.7, 0.2, 0]} castShadow>
        <boxGeometry args={[0.05, 0.4, 0.05]} />
        <meshStandardMaterial color={hovered ? '#6a6a6a' : '#4a4a4a'} metalness={0.8} roughness={0.3} />
      </mesh>
      <mesh position={[0.7, 0.2, 0]} castShadow>
        <boxGeometry args={[0.05, 0.4, 0.05]} />
        <meshStandardMaterial color={hovered ? '#6a6a6a' : '#4a4a4a'} metalness={0.8} roughness={0.3} />
      </mesh>
      {/* Wheel slots (U-shaped holders) */}
      {[-0.5, -0.25, 0, 0.25, 0.5].map((x, i) => (
        <group key={i} position={[x, 0, 0]}>
          {/* Left arm of U */}
          <mesh position={[0, 0.25, -0.08]} castShadow>
            <boxGeometry args={[0.03, 0.35, 0.03]} />
            <meshStandardMaterial color="#5a5a5a" metalness={0.7} roughness={0.4} />
          </mesh>
          {/* Right arm of U */}
          <mesh position={[0, 0.25, 0.08]} castShadow>
            <boxGeometry args={[0.03, 0.35, 0.03]} />
            <meshStandardMaterial color="#5a5a5a" metalness={0.7} roughness={0.4} />
          </mesh>
          {/* Bottom of U */}
          <mesh position={[0, 0.07, 0]} castShadow>
            <boxGeometry args={[0.03, 0.03, 0.19]} />
            <meshStandardMaterial color="#5a5a5a" metalness={0.7} roughness={0.4} />
          </mesh>
        </group>
      ))}

      {/* Deposited bikes */}
      {blueDeposited && <ParkedBike color="#4169E1" position={[-0.25, 0, 0.15]} />}
      {yellowDeposited && <ParkedBike color="#FFD700" position={[0.25, 0, 0.15]} />}

      {/* Sign post */}
      <mesh position={[0.9, 0.5, 0]} castShadow>
        <cylinderGeometry args={[0.02, 0.02, 0.6, 8]} />
        <meshStandardMaterial color="#3a3a3a" metalness={0.6} />
      </mesh>
      {/* Sign */}
      <mesh position={[0.9, 0.75, 0]} castShadow>
        <boxGeometry args={[0.3, 0.2, 0.02]} />
        <meshStandardMaterial color={hovered ? '#4169E1' : '#2244aa'} />
      </mesh>
      {/* Sign text indicator (bicycle icon shape) */}
      <mesh position={[0.9, 0.75, 0.015]}>
        <torusGeometry args={[0.04, 0.008, 8, 16]} />
        <meshBasicMaterial color="#ffffff" />
      </mesh>
      <mesh position={[0.85, 0.75, 0.015]}>
        <torusGeometry args={[0.04, 0.008, 8, 16]} />
        <meshBasicMaterial color="#ffffff" />
      </mesh>
    </group>
  );
}

// ===== NPC DEMAGORDON (Boss démagogue) =====
// With built-in explosion animation

interface BodyPart {
  id: string;
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  rotation: THREE.Euler;
  rotationSpeed: THREE.Vector3;
  scale: number;
  color: string;
  emissive?: string;
  emissiveIntensity?: number;
  geometry: 'cylinder' | 'box' | 'sphere' | 'cone' | 'torus';
  geometryArgs: number[];
  originalPosition: [number, number, number];
  originalRotation: [number, number, number];
}

function generateExplosionParts(): BodyPart[] {
  const parts: BodyPart[] = [];
  const bodyColor = '#2a1a2a';
  const petalColor = '#4a1a2a';
  const glowColor = '#ff2244';

  // Helper to create part with explosion velocity
  const createPart = (
    id: string,
    pos: [number, number, number],
    rot: [number, number, number],
    geometry: BodyPart['geometry'],
    geometryArgs: number[],
    color: string,
    emissive?: string,
    emissiveIntensity?: number
  ): BodyPart => {
    const angle = Math.random() * Math.PI * 2;
    const upForce = 3 + Math.random() * 6;
    const outForce = 2 + Math.random() * 4;
    return {
      id,
      position: new THREE.Vector3(pos[0], pos[1], pos[2]),
      velocity: new THREE.Vector3(
        Math.cos(angle) * outForce + (pos[0] * 2),
        upForce,
        Math.sin(angle) * outForce + (pos[2] * 2)
      ),
      rotation: new THREE.Euler(rot[0], rot[1], rot[2]),
      rotationSpeed: new THREE.Vector3(
        (Math.random() - 0.5) * 15,
        (Math.random() - 0.5) * 15,
        (Math.random() - 0.5) * 15
      ),
      scale: 1,
      color,
      emissive,
      emissiveIntensity,
      geometry,
      geometryArgs,
      originalPosition: pos,
      originalRotation: rot,
    };
  };

  // Legs
  parts.push(createPart('leg-l', [-0.2, 0.4, 0], [0, 0, 0], 'cylinder', [0.12, 0.15, 0.8, 8], bodyColor));
  parts.push(createPart('leg-r', [0.2, 0.4, 0], [0, 0, 0], 'cylinder', [0.12, 0.15, 0.8, 8], bodyColor));
  parts.push(createPart('foot-l', [-0.2, 0.05, 0.1], [0, 0, 0], 'box', [0.15, 0.1, 0.25], bodyColor));
  parts.push(createPart('foot-r', [0.2, 0.05, 0.1], [0, 0, 0], 'box', [0.15, 0.1, 0.25], bodyColor));

  // Torso
  parts.push(createPart('torso', [0, 1.1, 0], [0, 0, 0], 'cylinder', [0.25, 0.2, 0.9, 8], bodyColor));
  parts.push(createPart('chest', [0, 1.2, 0.1], [0, 0, 0], 'sphere', [0.2, 8, 8], bodyColor));

  // Arms
  parts.push(createPart('arm-l', [-0.35, 1.3, 0], [0, 0, 0.4], 'cylinder', [0.08, 0.1, 0.5, 6], bodyColor));
  parts.push(createPart('arm-r', [0.35, 1.3, 0], [0, 0, -0.4], 'cylinder', [0.08, 0.1, 0.5, 6], bodyColor));
  parts.push(createPart('forearm-l', [-0.55, 1.0, 0], [0, 0, 0.2], 'cylinder', [0.06, 0.08, 0.45, 6], bodyColor));
  parts.push(createPart('forearm-r', [0.55, 1.0, 0], [0, 0, -0.2], 'cylinder', [0.06, 0.08, 0.45, 6], bodyColor));
  parts.push(createPart('hand-l', [-0.65, 0.8, 0], [0, 0, 0], 'sphere', [0.08, 6, 6], bodyColor));
  parts.push(createPart('hand-r', [0.65, 0.8, 0], [0, 0, 0], 'sphere', [0.08, 6, 6], bodyColor));

  // Neck
  parts.push(createPart('neck', [0, 1.6, 0], [0, 0, 0], 'cylinder', [0.12, 0.15, 0.25, 8], bodyColor));

  // Head
  parts.push(createPart('head', [0, 1.85, 0], [0, 0, 0], 'sphere', [0.2, 12, 12], bodyColor));

  // Petals (5)
  for (let i = 0; i < 5; i++) {
    const angle = (i / 5) * Math.PI * 2;
    const x = Math.sin(angle) * 0.15;
    const z = Math.cos(angle) * 0.15;
    parts.push(createPart(`petal-${i}`, [x, 2.05, z], [Math.PI / 3, angle, 0], 'box', [0.12, 0.35, 0.04], petalColor, glowColor, 0.3));
    parts.push(createPart(`petal-tip-${i}`, [x * 1.5, 2.25, z * 1.5], [Math.PI / 3 + 0.2, angle, 0], 'cone', [0.06, 0.15, 4], petalColor, glowColor, 0.4));
  }

  // Mouth interior
  parts.push(createPart('mouth', [0, 1.95, 0], [0, 0, 0], 'sphere', [0.12, 8, 8], '#4a0a0a', glowColor, 1));

  // Gore particles (small spheres)
  for (let i = 0; i < 20; i++) {
    const px = (Math.random() - 0.5) * 0.5;
    const py = 0.8 + Math.random() * 1.2;
    const pz = (Math.random() - 0.5) * 0.5;
    parts.push(createPart(`gore-${i}`, [px, py, pz], [0, 0, 0], 'sphere', [0.03 + Math.random() * 0.05, 6, 6], '#8a2a2a', glowColor, 0.5));
  }

  return parts;
}

export function NPCDemagordonModel({ world, hovered }: ModelProps) {
  const { visualEffect, setVisualEffect, solveRiddle } = useGameStore();
  const isExploding = visualEffect === 'demagordon_explosion';

  const bodyColor = '#2a1a2a';
  const petalColor = '#4a1a2a';
  const glowColor = '#ff2244';
  const glowIntensity = hovered ? 1.5 : 0.8;

  // Explosion state
  const [explosionPhase, setExplosionPhase] = useState<'idle' | 'buildup' | 'exploding' | 'settling' | 'done'>('idle');
  const [opacity, setOpacity] = useState(1);
  const partsRef = useRef<BodyPart[]>([]);
  const startTimeRef = useRef(0);
  const buildupScaleRef = useRef(1);
  const flashIntensityRef = useRef(0);

  // Initialize explosion when triggered
  useEffect(() => {
    if (isExploding && explosionPhase === 'idle') {
      partsRef.current = generateExplosionParts();
      setExplosionPhase('buildup');
      startTimeRef.current = 0;
      buildupScaleRef.current = 1;
      setOpacity(1);
    }
  }, [isExploding, explosionPhase]);

  // Animation loop
  useFrame((state, delta) => {
    if (explosionPhase === 'idle') return;

    if (startTimeRef.current === 0) {
      startTimeRef.current = state.clock.elapsedTime;
    }

    const elapsed = state.clock.elapsedTime - startTimeRef.current;

    // Phase timings
    const buildupDuration = 1.2;
    const explodeDuration = 2.5;
    const settlingDuration = 1.5;
    const fadeDuration = 1.0;

    if (explosionPhase === 'buildup') {
      // Pulsing and shaking before explosion
      buildupScaleRef.current = 1 + Math.sin(elapsed * 25) * 0.15 * (elapsed / buildupDuration);
      flashIntensityRef.current = (elapsed / buildupDuration) * 0.5;

      if (elapsed >= buildupDuration) {
        setExplosionPhase('exploding');
      }
    }

    if (explosionPhase === 'exploding' || explosionPhase === 'settling') {
      const gravity = 12;
      const friction = 0.98;
      const bounceFactor = 0.25;

      partsRef.current.forEach((part) => {
        // Apply gravity
        part.velocity.y -= gravity * delta;

        // Apply velocity
        part.position.add(part.velocity.clone().multiplyScalar(delta));

        // Apply rotation
        part.rotation.x += part.rotationSpeed.x * delta;
        part.rotation.y += part.rotationSpeed.y * delta;
        part.rotation.z += part.rotationSpeed.z * delta;

        // Bounce on ground
        if (part.position.y < 0.05) {
          part.position.y = 0.05;
          part.velocity.y = Math.abs(part.velocity.y) * bounceFactor;
          part.velocity.x *= friction;
          part.velocity.z *= friction;
          part.rotationSpeed.multiplyScalar(0.7);
        }

        // Air friction
        part.velocity.multiplyScalar(0.995);
      });

      const explosionTime = elapsed - buildupDuration;
      flashIntensityRef.current = Math.max(0, 1 - explosionTime * 2);

      if (explosionPhase === 'exploding' && explosionTime >= explodeDuration) {
        setExplosionPhase('settling');
      }

      if (explosionPhase === 'settling' && explosionTime >= explodeDuration + settlingDuration) {
        setExplosionPhase('done');
      }
    }

    if (explosionPhase === 'done') {
      const fadeTime = elapsed - buildupDuration - explodeDuration - settlingDuration;
      const newOpacity = Math.max(0, 1 - fadeTime / fadeDuration);
      setOpacity(newOpacity);

      if (fadeTime >= fadeDuration) {
        // Animation complete - mark as defeated and clear effect
        solveRiddle('demagordon_defeated');
        setVisualEffect(null);
        setExplosionPhase('idle');
      }
    }
  });

  // Render exploding parts
  if (explosionPhase === 'exploding' || explosionPhase === 'settling' || explosionPhase === 'done') {
    return (
      <group>
        {partsRef.current.map((part) => (
          <mesh
            key={part.id}
            position={[part.position.x, part.position.y, part.position.z]}
            rotation={[part.rotation.x, part.rotation.y, part.rotation.z]}
            scale={[part.scale, part.scale, part.scale]}
          >
            {part.geometry === 'cylinder' && <cylinderGeometry args={part.geometryArgs as [number, number, number, number]} />}
            {part.geometry === 'box' && <boxGeometry args={part.geometryArgs as [number, number, number]} />}
            {part.geometry === 'sphere' && <sphereGeometry args={part.geometryArgs as [number, number, number]} />}
            {part.geometry === 'cone' && <coneGeometry args={part.geometryArgs as [number, number, number]} />}
            <meshStandardMaterial
              color={part.color}
              emissive={part.emissive || part.color}
              emissiveIntensity={part.emissiveIntensity || 0.2}
              transparent
              opacity={opacity}
            />
          </mesh>
        ))}

        {/* Explosion flash */}
        {flashIntensityRef.current > 0 && (
          <pointLight
            position={[0, 1.5, 0]}
            color="#ff4444"
            intensity={flashIntensityRef.current * 30}
            distance={15}
            decay={2}
          />
        )}

        {/* Blood splatter on ground */}
        <mesh position={[0, 0.01, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[2 * Math.min(1, opacity + 0.5), 32]} />
          <meshBasicMaterial color="#4a0a0a" transparent opacity={opacity * 0.7} />
        </mesh>
      </group>
    );
  }

  // Render buildup (pulsing/shaking before explosion)
  if (explosionPhase === 'buildup') {
    const scale = buildupScaleRef.current;
    const shake = Math.sin(Date.now() * 0.05) * 0.02;

    return (
      <group scale={[scale, scale, scale]} position={[shake, 0, shake * 0.5]}>
        {/* Same normal model but with pulsing glow */}
        <DemagordonNormalModel hovered={hovered} glowBoost={flashIntensityRef.current * 3} />

        {/* Warning glow */}
        <pointLight
          position={[0, 1.5, 0]}
          color="#ff2244"
          intensity={5 + flashIntensityRef.current * 15}
          distance={8}
          decay={2}
        />
      </group>
    );
  }

  // Normal rendering (not exploding)
  return <DemagordonNormalModel hovered={hovered} glowBoost={0} />;
}

// Separate component for normal (non-exploding) Demagordon
function DemagordonNormalModel({ hovered, glowBoost = 0 }: { hovered: boolean; glowBoost?: number }) {
  const bodyColor = '#2a1a2a';
  const petalColor = '#4a1a2a';
  const glowColor = '#ff2244';
  const glowIntensity = (hovered ? 1.5 : 0.8) + glowBoost;

  return (
    <group>
      {/* Hitbox pour clic */}
      <mesh position={[0, 1.2, 0]}>
        <boxGeometry args={[1.5, 2.8, 1.5]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>

      {/* ===== JAMBES ===== */}
      {/* Jambe gauche */}
      <mesh position={[-0.2, 0.4, 0]} castShadow>
        <cylinderGeometry args={[0.12, 0.15, 0.8, 8]} />
        <meshStandardMaterial color={bodyColor} roughness={0.9} />
      </mesh>
      {/* Pied gauche - griffes */}
      <mesh position={[-0.2, 0.05, 0.1]} castShadow>
        <boxGeometry args={[0.15, 0.1, 0.25]} />
        <meshStandardMaterial color={bodyColor} roughness={0.95} />
      </mesh>
      {[-0.08, 0, 0.08].map((offset, i) => (
        <mesh key={`claw-l-${i}`} position={[-0.2 + offset * 0.5, 0.02, 0.2]} rotation={[0.5, 0, 0]} castShadow>
          <coneGeometry args={[0.03, 0.12, 4]} />
          <meshStandardMaterial color="#1a0a1a" roughness={0.8} />
        </mesh>
      ))}

      {/* Jambe droite */}
      <mesh position={[0.2, 0.4, 0]} castShadow>
        <cylinderGeometry args={[0.12, 0.15, 0.8, 8]} />
        <meshStandardMaterial color={bodyColor} roughness={0.9} />
      </mesh>
      {/* Pied droit - griffes */}
      <mesh position={[0.2, 0.05, 0.1]} castShadow>
        <boxGeometry args={[0.15, 0.1, 0.25]} />
        <meshStandardMaterial color={bodyColor} roughness={0.95} />
      </mesh>
      {[-0.08, 0, 0.08].map((offset, i) => (
        <mesh key={`claw-r-${i}`} position={[0.2 + offset * 0.5, 0.02, 0.2]} rotation={[0.5, 0, 0]} castShadow>
          <coneGeometry args={[0.03, 0.12, 4]} />
          <meshStandardMaterial color="#1a0a1a" roughness={0.8} />
        </mesh>
      ))}

      {/* ===== TORSE ===== */}
      <mesh position={[0, 1.1, 0]} castShadow>
        <cylinderGeometry args={[0.25, 0.2, 0.9, 8]} />
        <meshStandardMaterial color={bodyColor} roughness={0.85} />
      </mesh>
      {/* Poitrine musclée */}
      <mesh position={[0, 1.2, 0.1]} castShadow>
        <sphereGeometry args={[0.2, 8, 8, 0, Math.PI]} />
        <meshStandardMaterial color={bodyColor} roughness={0.9} />
      </mesh>
      {/* Côtes visibles */}
      {[0.9, 1.0, 1.1].map((y, i) => (
        <mesh key={`rib-${i}`} position={[0, y, 0.15]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.18 - i * 0.02, 0.02, 4, 12, Math.PI]} />
          <meshStandardMaterial color="#1a0a1a" roughness={0.95} />
        </mesh>
      ))}

      {/* ===== BRAS ===== */}
      {/* Bras gauche */}
      <group position={[-0.35, 1.3, 0]}>
        <mesh rotation={[0, 0, 0.4]} castShadow>
          <cylinderGeometry args={[0.08, 0.1, 0.5, 6]} />
          <meshStandardMaterial color={bodyColor} roughness={0.9} />
        </mesh>
        {/* Avant-bras */}
        <mesh position={[-0.2, -0.3, 0]} rotation={[0, 0, 0.2]} castShadow>
          <cylinderGeometry args={[0.06, 0.08, 0.45, 6]} />
          <meshStandardMaterial color={bodyColor} roughness={0.9} />
        </mesh>
        {/* Main avec griffes */}
        <mesh position={[-0.3, -0.5, 0]} castShadow>
          <sphereGeometry args={[0.08, 6, 6]} />
          <meshStandardMaterial color={bodyColor} roughness={0.9} />
        </mesh>
        {[0, 1, 2, 3].map((i) => (
          <mesh key={`finger-l-${i}`} position={[-0.35 + i * 0.03, -0.6, 0.02 - i * 0.02]} rotation={[0.3, 0, 0.1]} castShadow>
            <coneGeometry args={[0.015, 0.12, 4]} />
            <meshStandardMaterial color="#1a0a1a" roughness={0.8} />
          </mesh>
        ))}
      </group>

      {/* Bras droit */}
      <group position={[0.35, 1.3, 0]}>
        <mesh rotation={[0, 0, -0.4]} castShadow>
          <cylinderGeometry args={[0.08, 0.1, 0.5, 6]} />
          <meshStandardMaterial color={bodyColor} roughness={0.9} />
        </mesh>
        {/* Avant-bras */}
        <mesh position={[0.2, -0.3, 0]} rotation={[0, 0, -0.2]} castShadow>
          <cylinderGeometry args={[0.06, 0.08, 0.45, 6]} />
          <meshStandardMaterial color={bodyColor} roughness={0.9} />
        </mesh>
        {/* Main avec griffes */}
        <mesh position={[0.3, -0.5, 0]} castShadow>
          <sphereGeometry args={[0.08, 6, 6]} />
          <meshStandardMaterial color={bodyColor} roughness={0.9} />
        </mesh>
        {[0, 1, 2, 3].map((i) => (
          <mesh key={`finger-r-${i}`} position={[0.25 + i * 0.03, -0.6, 0.02 - i * 0.02]} rotation={[0.3, 0, -0.1]} castShadow>
            <coneGeometry args={[0.015, 0.12, 4]} />
            <meshStandardMaterial color="#1a0a1a" roughness={0.8} />
          </mesh>
        ))}
      </group>

      {/* ===== COU ===== */}
      <mesh position={[0, 1.6, 0]} castShadow>
        <cylinderGeometry args={[0.12, 0.15, 0.25, 8]} />
        <meshStandardMaterial color={bodyColor} roughness={0.9} />
      </mesh>

      {/* ===== TÊTE EN FLEUR (DEMOGORGON STYLE) ===== */}
      {/* Base de la tête */}
      <mesh position={[0, 1.85, 0]} castShadow>
        <sphereGeometry args={[0.2, 12, 12]} />
        <meshStandardMaterial color={bodyColor} roughness={0.85} />
      </mesh>

      {/* Pétales de la fleur-bouche (5 pétales) */}
      {[0, 1, 2, 3, 4].map((i) => {
        const angle = (i / 5) * Math.PI * 2;
        const petalRotation = Math.PI / 3; // Ouverture des pétales
        return (
          <group key={`petal-${i}`} position={[0, 1.9, 0]} rotation={[0, angle, 0]}>
            {/* Pétale principal */}
            <mesh position={[0, 0.15, 0.15]} rotation={[petalRotation, 0, 0]} castShadow>
              <boxGeometry args={[0.12, 0.35, 0.04]} />
              <meshStandardMaterial
                color={petalColor}
                roughness={0.7}
                emissive={glowColor}
                emissiveIntensity={glowIntensity * 0.3}
              />
            </mesh>
            {/* Pointe du pétale */}
            <mesh position={[0, 0.35, 0.25]} rotation={[petalRotation + 0.2, 0, 0]} castShadow>
              <coneGeometry args={[0.06, 0.15, 4]} />
              <meshStandardMaterial
                color={petalColor}
                roughness={0.7}
                emissive={glowColor}
                emissiveIntensity={glowIntensity * 0.4}
              />
            </mesh>
            {/* Dents sur les pétales */}
            {[0.08, 0.18, 0.28].map((offset, j) => (
              <mesh key={`tooth-${i}-${j}`} position={[(j % 2 === 0 ? 0.04 : -0.04), offset, 0.17]} rotation={[petalRotation * 0.8, 0, 0]} castShadow>
                <coneGeometry args={[0.015, 0.06, 3]} />
                <meshStandardMaterial color="#f0e0e0" roughness={0.5} />
              </mesh>
            ))}
          </group>
        );
      })}

      {/* Intérieur de la bouche (gorge rouge) */}
      <mesh position={[0, 1.95, 0]}>
        <sphereGeometry args={[0.12, 8, 8]} />
        <meshStandardMaterial
          color="#4a0a0a"
          emissive={glowColor}
          emissiveIntensity={glowIntensity}
        />
      </mesh>

      {/* ===== DÉTAILS BOSS - AURA DÉMONIAQUE ===== */}
      {/* Particules flottantes autour */}
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const angle = (i / 6) * Math.PI * 2;
        const radius = 0.5 + (i % 2) * 0.2;
        const height = 1.2 + Math.sin(i * 1.5) * 0.4;
        return (
          <mesh key={`aura-${i}`} position={[
            Math.cos(angle) * radius,
            height,
            Math.sin(angle) * radius
          ]}>
            <sphereGeometry args={[0.04, 6, 6]} />
            <meshStandardMaterial
              color={glowColor}
              emissive={glowColor}
              emissiveIntensity={glowIntensity * 1.5}
              transparent
              opacity={0.7}
            />
          </mesh>
        );
      })}

      {/* Lumière centrale menaçante */}
      <pointLight
        position={[0, 1.9, 0.2]}
        intensity={glowIntensity * 2}
        color={glowColor}
        distance={4}
      />

      {/* Hover effect - lumière plus intense */}
      {hovered && (
        <>
          <pointLight position={[0, 1.5, 0.5]} intensity={1.5} color={glowColor} distance={3} />
          {/* Aura supplémentaire au hover */}
          <mesh position={[0, 1.2, 0]}>
            <sphereGeometry args={[0.8, 12, 12]} />
            <meshStandardMaterial
              color={glowColor}
              emissive={glowColor}
              emissiveIntensity={0.5}
              transparent
              opacity={0.15}
            />
          </mesh>
        </>
      )}
    </group>
  );
}

// ============================================
// MODEL REGISTRY
// ============================================

export const strangerThingsModels: ModelRegistry = {
  // 80s Props & Items
  vintage_bike: VintageBikeModel,
  bike_rack: BikeRackModel,
  walkie_talkie: WalkieTalkieModel,
  flashlight: FlashlightModel,
  mailbox: MailboxModel,
  arcade_cabinet: ArcadeCabinetModel,
  eggo_box: EggoBoxModel,

  // 80s Vehicles
  car_80s: Car80sModel,
  car_wrecked: CarWreckedModel,
  school_bus: SchoolBusModel,

  // Hawkins Buildings
  hawkins_school: HawkinsSchoolModel,
  upside_down_building: UpsideDownBuildingModel,

  // NPCs
  npc_dustin: NPCDustinModel,
  npc_erica: NPCEricaModel,
  npc_demagordon: NPCDemagordonModel,

  // Upside Down Elements
  dimensional_rift: DimensionalRiftModel,
  dead_tree: DeadTreeModel,
  oak_tree: OakTreeModel,
};
